import React, { useState } from "react";
import { ShieldCheck, FileUp, Send, CheckCircle2, XCircle, AlertCircle, Search } from "lucide-react";
import Alert from "../../components/ui/Alert.tsx";

// Estructura de datos unificada para la comparación
interface XMLData {
  ruc: string;
  tipo: string;
  serie: string;
  numero: string;
  fecha: string;
  monto: string;
  moneda: string;
  ordenCompra?: string; // Campo adicional para Ariba
}

const ComparadorMultiXML = () => {
  const [dataA, setDataA] = useState<XMLData | null>(null);
  const [dataB, setDataB] = useState<XMLData | null>(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");

  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 5000);
  };

  // --- LÓGICA SUNAT (Lado A) ---
  const extraerSunat = (xmlDoc: Document): XMLData | null => {
    const getVal = (path: string) => xmlDoc.evaluate(path, xmlDoc, null, XPathResult.STRING_TYPE, null).stringValue;

    const ID = getVal("/*/*[local-name()='ID']");
    const [serie, numero] = ID ? ID.split("-") : ["", ""];

    return {
      ruc: getVal("/*/*[local-name()='AccountingSupplierParty']/*[local-name()='Party']/*[local-name()='PartyIdentification']/*[local-name()='ID']"),
      tipo: getVal("/*/*[local-name()='InvoiceTypeCode']"),
      fecha: getVal("/*/*[local-name()='IssueDate']"),
      monto: getVal("/*/*[local-name()='LegalMonetaryTotal']/*[local-name()='PayableAmount']"),
      moneda: getVal("/*/*[local-name()='DocumentCurrencyCode']"),
      serie: serie || "",
      numero: numero || ""
    };
  };

  // --- LÓGICA SAP ARIBA (Lado B) ---
  const extraerAriba = (xmlDoc: Document): XMLData | null => {
    const getAttr = (path: string) => {
      const res = xmlDoc.evaluate(path, xmlDoc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
      return res ? (res as Attr).nodeValue || (res as Element).textContent || "" : "";
    };

    const fullID = getAttr("//InvoiceDetailRequestHeader/@invoiceID");
    // Intentamos separar Serie y Número si Ariba trae el guion
    const [serie, numero] = fullID.includes("-") ? fullID.split("-") : ["", fullID];

    return {
      ruc: getAttr("//Extrinsic[@name='supplierVatID']"),
      tipo: "01", // Por defecto factura en este flujo
      fecha: getAttr("//InvoiceDetailRequestHeader/@invoiceDate").split('T')[0],
      monto: getAttr("//InvoiceDetailSummary/DueAmount/Money"),
      moneda: getAttr("//InvoiceDetailSummary/DueAmount/Money/@currency"),
      serie: serie,
      numero: numero,
      ordenCompra: getAttr("//SupplierOrderInfo/@orderID")
    };
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, side: 'A' | 'B') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");

      let parsed: XMLData | null = null;

      // Detectamos el tipo de XML por el nodo raíz
      if (xmlDoc.documentElement.nodeName.includes("cXML")) {
        parsed = extraerAriba(xmlDoc);
      } else {
        parsed = extraerSunat(xmlDoc);
      }

      if (!parsed?.ruc && !parsed?.monto) {
        showAlert(`Error al procesar el archivo ${side}. Verifica la estructura.`, "error");
        return;
      }

      side === 'A' ? setDataA(parsed) : setDataB(parsed);
    };
    reader.readAsText(file);
  };

  const compararCampos = () => {
    if (!dataA || !dataB) return [];
    
    // Función para limpiar montos (quitar comas o espacios si los hay)
    const cleanNum = (val: string) => parseFloat(val).toFixed(2);

    return [
      { label: "RUC Emisor", valA: dataA.ruc, valB: dataB.ruc },
      { label: "Serie", valA: dataA.serie, valB: dataB.serie },
      { label: "Número", valA: dataA.numero, valB: dataB.numero },
      { label: "Fecha Emisión", valA: dataA.fecha, valB: dataB.fecha },
      { label: "Moneda", valA: dataA.moneda, valB: dataB.moneda },
      { label: "Importe Total", valA: cleanNum(dataA.monto), valB: cleanNum(dataB.monto) },
    ];
  };

  const resultados = compararCampos();
  const todoOK = resultados.length > 0 && resultados.every(r => r.valA === r.valB);

  const labelClass = "block text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-1 ml-1";
  const displayValueClass = "w-full bg-black/40 border border-white/5 rounded-xl p-3 text-sm text-gray-300 font-mono shadow-inner";

  return (
    <main className="min-h-screen p-4 md:p-8 text-white">
      <header className="max-w-7xl mx-auto mb-10 text-center">
        <h1 className="text-4xl font-black bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-600 tracking-tighter">
          ONEHUB <span className="text-white/10 font-thin">|</span> INTEGRACIÓN ARIBA
        </h1>
        <p className="text-gray-500 mt-2 font-medium">Validación cruzada SUNAT vs cXML</p>
      </header>

      <div className="max-w-7xl mx-auto space-y-8">
        <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LADO A: SUNAT */}
          <section className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5 text-cyan-400"><Search size={120} /></div>
            <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4 text-cyan-400">
              <FileUp size={24} /> <h2 className="font-bold uppercase tracking-tighter">XML SUNAT (UBL)</h2>
            </div>
            <input type="file" onChange={(e) => handleFileUpload(e, 'A')} className="mb-6 block w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-6 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-cyan-500 file:text-[#fff] hover:file:bg-cyan-400 transition-all cursor-pointer" />
            
            {dataA && (
              <div className="grid grid-cols-2 gap-4 animate-fadeIn relative z-10">
                <div className="col-span-2"><label className={labelClass}>RUC Emisor</label><div className={displayValueClass}>{dataA.ruc}</div></div>
                <div><label className={labelClass}>Serie-Número</label><div className={displayValueClass}>{dataA.serie}-{dataA.numero}</div></div>
                <div><label className={labelClass}>Fecha</label><div className={displayValueClass}>{dataA.fecha}</div></div>
                <div className="col-span-2"><label className={labelClass}>Total Documento</label><div className={displayValueClass}>{dataA.moneda} {dataA.monto}</div></div>
              </div>
            )}
          </section>

          {/* LADO B: ARIBA */}
          <section className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5 text-blue-400"><ShieldCheck size={120} /></div>
            <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4 text-blue-400">
              <FileUp size={24} /> <h2 className="font-bold uppercase tracking-tighter">XML ARIBA (cXML)</h2>
            </div>
            <input type="file" onChange={(e) => handleFileUpload(e, 'B')} className="mb-6 block w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-6 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-[#fff] hover:file:bg-blue-500 transition-all cursor-pointer" />
            
            {dataB && (
              <div className="grid grid-cols-2 gap-4 animate-fadeIn relative z-10">
                <div className="col-span-2"><label className={labelClass}>Supplier VAT ID</label><div className={displayValueClass}>{dataB.ruc}</div></div>
                <div><label className={labelClass}>Invoice ID</label><div className={displayValueClass}>{dataB.serie}-{dataB.numero}</div></div>
                <div><label className={labelClass}>Order ID</label><div className={displayValueClass}>{dataB.ordenCompra}</div></div>
                <div className="col-span-2"><label className={labelClass}>Due Amount</label><div className={displayValueClass}>{dataB.moneda} {dataB.monto}</div></div>
              </div>
            )}
          </section>
        </div>

        {/* RESULTADOS DE COMPARACIÓN */}
        {dataA && dataB && (
          <section className="bg-[rgb(var(--c-bg))]/80 backdrop-blur-md border border-white/10 rounded-[3rem] p-10 shadow-3xl animate-slideUp">
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black flex items-center gap-3">
                <ShieldCheck className="text-emerald-400" size={32} /> PANEL DE DISCREPANCIAS
                </h2>
                <div className={`px-4 py-1 rounded-full text-[10px] font-black uppercase ${todoOK ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {todoOK ? "Sincronizado" : "Error de cruce"}
                </div>
            </div>
            
            <div className="grid gap-3">
              {resultados.map((res, index) => (
                <div key={index} className={`group flex items-center justify-between p-5 rounded-2xl border transition-all ${res.valA === res.valB ? 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/30' : 'bg-red-500/5 border-red-500/10 hover:border-red-500/30'}`}>
                  <div className="flex flex-col">
                    <span className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">{res.label}</span>
                    <div className="flex items-center gap-4">
                        <span className="text-sm font-mono text-gray-400">{res.valA}</span>
                        <div className="h-px w-4 bg-gray-800"></div>
                        <span className={`text-sm font-mono font-bold ${res.valA === res.valB ? 'text-emerald-400' : 'text-red-400'}`}>{res.valB}</span>
                    </div>
                  </div>
                  {res.valA === res.valB ? <CheckCircle2 className="text-emerald-500 shadow-lg" size={24} /> : <XCircle className="text-red-500 animate-pulse" size={24} />}
                </div>
              ))}
            </div>

            <button
              disabled={!todoOK}
              className={`w-full mt-10 py-6 rounded-2xl font-black text-sm uppercase tracking-tighter transition-all flex items-center justify-center gap-4 ${todoOK ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:scale-[1.01] shadow-xl shadow-emerald-900/20' : 'bg-gray-800/50 text-gray-600 cursor-not-allowed border border-white/5'}`}
            >
              {todoOK ? <><Send size={20} /> Confirmar Posteo en Ariba Network</> : <><AlertCircle size={20} /> Datos inconsistentes para integración</>}
            </button>
          </section>
        )}
      </div>
    </main>
  );
};

export default ComparadorMultiXML;